import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { getCardBalance, getCardTransactions, getCardPendingLoads, markCardTransactionLoaded } from '../../../services/cardService';
import api from '../../../services/api';
import { getUser, isAdminUser } from '../../../services/authService';
import './CardTransactionsList.scss';

const CardTransactionsList = () => {
    const isAdmin = isAdminUser();
    const [programTabs, setProgramTabs] = useState([]);
    const [activeProgramTab, setActiveProgramTab] = useState('Pool');
    const [viewMode, setViewMode] = useState('pending'); // 'pending', 'search'
    const [pendingLoads, setPendingLoads] = useState([]);
    const [loadingPending, setLoadingPending] = useState(false);

    const [cardNumber, setCardNumber] = useState('');
    const [balanceInfo, setBalanceInfo] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);
    const [filterStatus, setFilterStatus] = useState('all'); // all, success, failed

    // Toast notification state
    const [toast, setToast] = useState(null); // { type: 'success' | 'error' | 'warning' | 'info', message: string }
    const [confirmDialog, setConfirmDialog] = useState(null); // { message, onConfirm }

    const normalizeProgramCategory = (programType) => (programType === 'Fitness' ? 'Fitness' : 'Pool');

    const resolveProgramTabs = useCallback(async () => {
        if (isAdmin) {
            setProgramTabs(['Pool', 'Fitness']);
            setActiveProgramTab('Pool');
            return;
        }

        try {
            const user = getUser();
            const assignedRegionIds = user?.assignedRegions || [];
            if (!assignedRegionIds.length) {
                setProgramTabs([]);
                return;
            }

            const response = await api.get('/api/sports/regions-with-facilities');
            const regions = response?.data || response || [];
            const matchedRegions = regions.filter(region => assignedRegionIds.includes(region.regionId));

            const hasFitness = matchedRegions.some(region =>
                (region.facilities || []).some(facility => (facility.typeName || '').toLowerCase().includes('fitness'))
            );
            const hasPool = matchedRegions.some(region =>
                (region.facilities || []).some(facility => {
                    const typeName = (facility.typeName || '').toLowerCase();
                    return typeName.includes('havuz') || typeName.includes('pool') || typeName.includes('yuzme') || typeName.includes('yüzme');
                })
            );

            const nextTabs = [];
            if (hasPool) nextTabs.push('Pool');
            if (hasFitness) nextTabs.push('Fitness');
            setProgramTabs(nextTabs);
            setActiveProgramTab(nextTabs[0] || 'Pool');
        } catch (error) {
            console.error('Bakiye sekmeleri belirlenemedi:', error);
            setProgramTabs(['Pool', 'Fitness']);
            setActiveProgramTab('Pool');
        }
    }, [isAdmin]);

    const showToast = useCallback((type, message) => {
        setToast({ type, message });
        setTimeout(() => setToast(null), 3500);
    }, []);

    useEffect(() => {
        resolveProgramTabs();
    }, [resolveProgramTabs]);

    const fetchPendingLoads = async () => {
        setLoadingPending(true);
        try {
            const res = await getCardPendingLoads();
            if (res?.isSuccess) {
                setPendingLoads(res.data || []);
            } else if (res?.data?.isSuccess) {
                // Fallback for non-intercepted responses
                setPendingLoads(res.data.data || []);
            }
        } catch (error) {
            console.error('Bekleyen yüklemeler çekilemedi:', error);
        } finally {
            setLoadingPending(false);
        }
    };

    useEffect(() => {
        if (viewMode === 'pending') {
            fetchPendingLoads();
        }
    }, [viewMode]);

    const handleMarkLoaded = (id) => {
        setConfirmDialog({
            message: 'Bu ödemenin karta yüklendiğini onaylıyor musunuz?',
            onConfirm: async () => {
                setConfirmDialog(null);
                try {
                    const res = await markCardTransactionLoaded(id);
                    if (res?.isSuccess || res?.data?.isSuccess) {
                        setPendingLoads(prev => prev.map(t =>
                            t.id === id ? { ...t, isLoadedLocally: true } : t
                        ));
                        showToast('success', 'Bakiye karta başarıyla yüklendi olarak işaretlendi.');
                    } else {
                        showToast('error', res?.resultMessage || res?.data?.resultMessage || 'İşlem başarısız.');
                    }
                } catch (error) {
                    showToast('error', 'İşlem sırasında hata oluştu.');
                }
            }
        });
    };

    const handleSearch = async () => {
        if (!cardNumber) {
            showToast('warning', 'Lütfen kart numarası giriniz.');
            return;
        }
        setLoading(true);
        setSearched(true);
        setBalanceInfo(null);
        setTransactions([]);
        setFilterStatus('all');

        try {
            // Get Balance
            const balRes = await getCardBalance(cardNumber);
            if (balRes?.isSuccess) {
                const balanceData = balRes.data;
                // isUnregistered (eski/yerel kart) ise program tipi kontrolü yapma
                if (!balRes.isUnregistered && balanceData?.programType) {
                    const cardProgram = normalizeProgramCategory(balanceData.programType);
                    if (cardProgram !== activeProgramTab) {
                        showToast('warning', `Bu kart ${cardProgram === 'Fitness' ? 'Fitness' : 'Havuz'} sekmesinde kayıtlıdır. Lütfen ilgili sekmeye geçiniz.`);
                        setLoading(false);
                        return;
                    }
                }
                setBalanceInfo(balanceData);
            } else if (balRes?.data?.isSuccess) {
                const balanceData = balRes.data.data;
                if (!balRes.data.isUnregistered && balanceData?.programType) {
                    const cardProgram = normalizeProgramCategory(balanceData.programType);
                    if (cardProgram !== activeProgramTab) {
                        showToast('warning', `Bu kart ${cardProgram === 'Fitness' ? 'Fitness' : 'Havuz'} sekmesinde kayıtlıdır. Lütfen ilgili sekmeye geçiniz.`);
                        setLoading(false);
                        return;
                    }
                }
                setBalanceInfo(balanceData);
            }

            // Get Transactions
            const txRes = await getCardTransactions();
            const allTx = txRes?.data || txRes || [];
            const txArray = Array.isArray(allTx) ? allTx : (allTx.data || []);
            // Kart numarasına göre filtrele, program tipi yoksa (eski kart) aktif sekmeye göre göster
            const filtered = txArray.filter(t => {
                if (t.cardNumber !== cardNumber) return false;
                if (!t.programType) return true; // program tipi yoksa göster
                return normalizeProgramCategory(t.programType) === activeProgramTab;
            });
            // Sort by date desc
            filtered.sort((a, b) => new Date(b.transactionDate) - new Date(a.transactionDate));
            setTransactions(filtered);
        } catch (error) {
            console.error('Sorgulama hatası:', error);
            showToast('error', 'Sorgulama sırasında hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 15;

    const filteredTransactions = useMemo(() => {
        let result = transactions;
        if (filterStatus === 'success') result = transactions.filter(t => t.isSuccess);
        else if (filterStatus === 'failed') result = transactions.filter(t => !t.isSuccess && t.transactionType !== 'Load');
        return result;
    }, [transactions, filterStatus]);

    const visiblePendingLoads = useMemo(
        () => pendingLoads.filter(tx => normalizeProgramCategory(tx.programType) === activeProgramTab),
        [pendingLoads, activeProgramTab]
    );

    const pendingLoadCount = useMemo(
        () => pendingLoads.filter(tx => !tx.isLoadedLocally).length,
        [pendingLoads]
    );

    const paginatedTransactions = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredTransactions.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredTransactions, currentPage]);

    const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);

    useEffect(() => {
        setCurrentPage(1);
    }, [filterStatus]);

    useEffect(() => {
        setBalanceInfo(null);
        setTransactions([]);
        setSearched(false);
        setCardNumber('');
    }, [activeProgramTab]);

    // Toast styles (re-implementing without heavy inline if possible, but keep the logic)
    const toastStyles = {
        success: { bg: 'linear-gradient(135deg, #10b981, #059669)', icon: '✓' },
        error: { bg: 'linear-gradient(135deg, #ef4444, #dc2626)', icon: '✕' },
        warning: { bg: 'linear-gradient(135deg, #f59e0b, #d97706)', icon: '⚠' },
        info: { bg: 'linear-gradient(135deg, #3b82f6, #2563eb)', icon: 'ℹ' }
    };

    return (
        <div className="card-transactions-container">
            {/* Toast Notification */}
            {toast && (
                <div className="sa-toast-wrapper" style={{
                    position: 'fixed',
                    top: '24px',
                    right: '24px',
                    zIndex: 9999,
                    animation: 'slideInRight 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                    minWidth: '320px',
                    maxWidth: '450px'
                }}>
                    <div style={{
                        background: toastStyles[toast.type]?.bg,
                        color: '#fff',
                        padding: '16px 20px',
                        borderRadius: '12px',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        fontSize: '14px',
                        fontWeight: '600',
                        backdropFilter: 'blur(10px)'
                    }}>
                        <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            background: 'rgba(255,255,255,0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '16px',
                            flexShrink: 0
                        }}>
                            {toastStyles[toast.type]?.icon}
                        </div>
                        <span style={{ flex: 1 }}>{toast.message}</span>
                        <button onClick={() => setToast(null)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', width: '24px', height: '24px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', flexShrink: 0 }}>✕</button>
                    </div>
                </div>
            )}

            {/* Confirm Dialog */}
            {confirmDialog && (
                <div className="sa-confirm-backdrop" style={{
                    position: 'fixed',
                    top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.5)',
                    backdropFilter: 'blur(4px)',
                    zIndex: 9998,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <div className="sa-confirm-modal" style={{
                        background: '#fff',
                        borderRadius: '16px',
                        padding: '32px',
                        maxWidth: '420px',
                        width: '90%',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
                    }}>
                        <h4 style={{ margin: '0 0 12px', fontSize: '18px', fontWeight: '700' }}>Onay Gerekli</h4>
                        <p style={{ color: '#64748b', fontSize: '14px', margin: '0 0 24px', lineHeight: '1.6' }}>{confirmDialog.message}</p>
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button onClick={() => setConfirmDialog(null)} className="btn-outline-primary" style={{ padding: '10px 20px', borderRadius: '10px', border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontWeight: '600' }}>İptal</button>
                            <button onClick={confirmDialog.onConfirm} className="btn-primary" style={{ padding: '10px 24px', borderRadius: '10px', border: 'none', background: '#3b82f6', color: '#fff', cursor: 'pointer', fontWeight: '600' }}>Evet, Onayla</button>
                        </div>
                    </div>
                </div>
            )}

            <div className="section-header">
                <h3>Bakiye ve İşlem Geçmişi</h3>
                <div className="view-mode-toggle">
                    <button
                        className={viewMode === 'pending' ? 'btn-primary' : 'btn-outline-primary'}
                        onClick={() => setViewMode('pending')}
                    >
                        <i className="fas fa-list-ul"></i> Bekleyen Yüklemeler
                        {pendingLoadCount > 0 && <span className="badge badge-danger">{pendingLoadCount}</span>}
                    </button>
                    <button
                        className={viewMode === 'search' ? 'btn-primary' : 'btn-outline-primary'}
                        onClick={() => setViewMode('search')}
                    >
                        <i className="fas fa-search"></i> Kart İle Sorgula
                    </button>
                </div>
            </div>

            {programTabs.length > 0 && (
                <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
                    {programTabs.map((tab) => (
                        <button
                            key={tab}
                            type="button"
                            onClick={() => setActiveProgramTab(tab)}
                            style={{
                                border: activeProgramTab === tab ? '2px solid #2563eb' : '1px solid #cbd5e1',
                                background: activeProgramTab === tab ? '#eff6ff' : '#fff',
                                color: activeProgramTab === tab ? '#1d4ed8' : '#475569',
                                borderRadius: '10px',
                                padding: '10px 16px',
                                fontWeight: '700',
                                cursor: 'pointer'
                            }}
                        >
                            {tab === 'Fitness' ? 'Fitness' : 'Havuz'}
                        </button>
                    ))}
                </div>
            )}

            {viewMode === 'pending' && (
                <div className="pending-loads-container">
                    {loadingPending ? (
                        <div className="empty-state"><i className="fas fa-spinner fa-spin"></i><p>Yükleniyor...</p></div>
                    ) : visiblePendingLoads.length > 0 ? (
                        <div className="table-responsive">
                            <table className="transactions-table">
                                <thead>
                                    <tr>
                                        <th>Tarih</th>
                                        <th>Tesis</th>
                                        <th>Kart Numarası</th>
                                        <th>Ad Soyad</th>
                                        <th>Yükleme Grubu</th>
                                        <th>Tutar</th>
                                        <th style={{ textAlign: 'right' }}>İşlem</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visiblePendingLoads.map(tx => (
                                        <tr key={tx.id}>
                                            <td>
                                                <div style={{ fontWeight: '700' }}>{new Date(tx.transactionDate).toLocaleDateString('tr-TR')}</div>
                                                <small style={{ color: '#64748b' }}>{new Date(tx.transactionDate).toLocaleTimeString('tr-TR')}</small>
                                            </td>
                                            <td>{tx.programDisplayName || (normalizeProgramCategory(tx.programType) === 'Fitness' ? 'Fitness' : 'Havuz')}</td>
                                            <td style={{ fontWeight: '700', color: '#3b82f6' }}>{tx.cardNumber}</td>
                                            <td style={{ fontWeight: '600' }}>{tx.fullName}</td>
                                            <td>{tx.paymentGroup === 'CardTopUp' ? 'Bakiye Yükleme' : tx.paymentGroup === 'CardApplicationFee' ? 'Kart Başvuru Ücreti' : tx.paymentGroup}</td>
                                            <td className="amount-cell amount-plus">+{tx.amount?.toFixed(2)} TL</td>
                                            <td style={{ textAlign: 'right' }}>
                                                {tx.isLoadedLocally ? (
                                                    <span className="loaded-locally-badge">
                                                        <i className="fas fa-check-circle"></i> Kontör yüklendi
                                                    </span>
                                                ) : (
                                                    <button
                                                        className="search-button"
                                                        style={{ padding: '8px 16px', fontSize: '0.85rem' }}
                                                        onClick={() => handleMarkLoaded(tx.id)}
                                                    >
                                                        <i className="fas fa-check"></i> Kart'a Yüklendi
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="empty-state">
                            <i className="fas fa-check-circle" style={{ color: '#10b981' }}></i>
                            <p>Bekleyen bakiye yüklemesi bulunmamaktadır.</p>
                        </div>
                    )}
                </div>
            )}

            {viewMode === 'search' && (
                <>
                    <div className="search-container">
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Kart Numarası (Örn: CARD123456)"
                            value={cardNumber}
                            onChange={(e) => setCardNumber(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                        />
                        <button
                            className="search-button"
                            onClick={handleSearch}
                            disabled={loading}
                        >
                            {loading ? <><i className="fas fa-spinner fa-spin"></i> Sorgulanıyor...</> : <><i className="fas fa-search"></i> Sorgula</>}
                        </button>
                    </div>

                    {balanceInfo && (
                        <div className="balance-card">
                            <div>
                                <span className="balance-label">Güncel Bakiye</span>
                                <h4 className="balance-amount">{balanceInfo.balance ? balanceInfo.balance.toFixed(2) : '0.00'}<small>TL</small></h4>
                            </div>
                            <div className="balance-user-info">
                                <h5>{balanceInfo.firstName} {balanceInfo.lastName}</h5>
                                <small>{balanceInfo.identityNumber} • {balanceInfo.programDisplayName || (normalizeProgramCategory(balanceInfo.programType) === 'Fitness' ? 'Fitness' : 'Havuz')}</small>
                            </div>
                        </div>
                    )}

                    {searched && !loading && (
                        <div className="transaction-history-section">
                            <div className="table-section-header">
                                <h4>İşlem Geçmişi</h4>
                                <div className="filter-container">
                                    <button className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`} onClick={() => setFilterStatus('all')}>
                                        Tümü ({transactions.length})
                                    </button>
                                    <button className={`filter-btn ${filterStatus === 'success' ? 'active success' : ''}`} onClick={() => setFilterStatus('success')}>
                                        Başarılı ({transactions.filter(t => t.isSuccess).length})
                                    </button>
                                    <button className={`filter-btn ${filterStatus === 'failed' ? 'active failed' : ''}`} onClick={() => setFilterStatus('failed')}>
                                        Başarısız ({transactions.filter(t => !t.isSuccess).length})
                                    </button>
                                </div>
                            </div>

                            {paginatedTransactions.length > 0 ? (
                                <>
                                    <div className="table-responsive">
                                        <table className="transactions-table">
                                            <thead>
                                                <tr>
                                                    <th>Tarih</th>
                                                    <th>İşlem Türü</th>
                                                    <th>Ödeme Türü</th>
                                                    <th>Durum</th>
                                                    <th>Tutar</th>
                                                    <th>Açıklama</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {paginatedTransactions.map((tx, index) => (
                                                    <tr key={index}>
                                                        <td>
                                                            <div style={{ fontWeight: '700' }}>{tx.transactionDate ? new Date(tx.transactionDate).toLocaleDateString('tr-TR') : '-'}</div>
                                                            <small style={{ color: '#64748b' }}>{tx.transactionDate ? new Date(tx.transactionDate).toLocaleTimeString('tr-TR') : '-'}</small>
                                                        </td>
                                                        <td>
                                                            <div className={`type-badge ${
                                                                tx.transactionType === 'Load' ? 'load' :
                                                                tx.transactionType === 'MembershipPurchase' ? 'membership' :
                                                                tx.transactionType === 'ApplicationFee' ? 'application' :
                                                                'spend'
                                                            }`}>
                                                                {tx.transactionType === 'Load' ? (
                                                                    <><i className="fas fa-arrow-down"></i> Bakiye Yükleme</>
                                                                ) : tx.transactionType === 'MembershipPurchase' ? (
                                                                    <><i className="fas fa-id-card"></i> Paket Satın Alma</>
                                                                ) : tx.transactionType === 'ApplicationFee' ? (
                                                                    <><i className="fas fa-file-alt"></i> Kart Başvurusu</>
                                                                ) : (
                                                                    <><i className="fas fa-arrow-up"></i> Harcama</>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span style={{
                                                                display: 'inline-block',
                                                                padding: '3px 10px',
                                                                borderRadius: '20px',
                                                                fontSize: '0.78rem',
                                                                fontWeight: '600',
                                                                background: tx.programType === 'Fitness' ? '#eff6ff' :
                                                                            tx.programType === 'WomenTraining' ? '#fdf2f8' : '#f0fdf4',
                                                                color: tx.programType === 'Fitness' ? '#2563eb' :
                                                                       tx.programType === 'WomenTraining' ? '#db2777' : '#16a34a'
                                                            }}>
                                                                {tx.programType === 'Fitness' ? '🏋️ Fitness' :
                                                                 tx.programType === 'WomenTraining' ? '👩 Kadın Eğitim' :
                                                                 tx.programDisplayName || '🏊 Havuz'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className={`status-badge ${tx.isSuccess ? 'success' : 'failed'}`}>
                                                                {tx.isSuccess ? 'Başarılı' : tx.transactionType === 'Load' ? 'Bekliyor' : 'Başarısız'}
                                                            </span>
                                                        </td>
                                                        <td className={`amount-cell ${tx.transactionType === 'Load' ? 'amount-plus' : 'amount-minus'}`}>
                                                            {tx.transactionType === 'Load' ? '+' : ''}{tx.amount ? tx.amount.toFixed(2) : 0} TL
                                                        </td>
                                                        <td style={{ maxWidth: '260px', fontSize: '0.85rem', color: '#64748b' }}>
                                                            {tx.description || '-'}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {totalPages > 1 && (
                                        <div className="pagination-wrapper">
                                            <button 
                                                className="page-btn" 
                                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                disabled={currentPage === 1}
                                            >
                                                <i className="fas fa-chevron-left"></i>
                                            </button>
                                            
                                            {[...Array(totalPages)].map((_, i) => (
                                                <button 
                                                    key={i + 1}
                                                    className={`page-btn ${currentPage === i + 1 ? 'active' : ''}`}
                                                    onClick={() => setCurrentPage(i + 1)}
                                                >
                                                    {i + 1}
                                                </button>
                                            )).slice(
                                                Math.max(0, Math.min(totalPages - 5, currentPage - 3)),
                                                Math.min(totalPages, Math.max(5, currentPage + 2))
                                            )}

                                            <button 
                                                className="page-btn" 
                                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                                disabled={currentPage === totalPages}
                                            >
                                                <i className="fas fa-chevron-right"></i>
                                            </button>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="empty-state">
                                    <i className="fas fa-history"></i>
                                    <p>Bu kriterlere uygun işlem geçmişi bulunamadı.</p>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default CardTransactionsList;
